import { Box, Stack } from '@mui/joy';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { FormField } from 'core/components';
import { DB_DATE_FORMAT } from 'core/constants';
import { InputType } from 'core/enums';
import { FormProvider, useFormContext } from 'core/states';
import { useGetVendorQuoteImportRangeReport } from 'dashboard/hooks';
import { Direction } from 'theme/enums';

import { ReportDiagnostics } from './ReportDiagnostics';
import { vendorQuoteLogSchema } from './schema';
import { StatCards } from './StatCards';
import { VendorQuoteLogTable } from './Table';
import { ReportError } from '../ReportError';

const FILTER_FIELD_WIDTH = 200;
// The grid fills the space left under the filters and cards; this floor keeps it
// usable on short screens, where the page scrolls instead (it matches the grid
// Wrapper's own minHeight).
const TABLE_MIN_HEIGHT = 300;

// Inclusive span limit, mirroring MAX_RANGE_DAYS in the BE helper: parsing is
// synchronous, so the pickers keep a wider range from being requested at all.
const MAX_RANGE_DAYS = 31;

const VendorQuoteLogContent = () => {
  const { t } = useTranslation();

  const { control, errors, setValue, watch } = useFormContext();

  const { user } = useAuthUser();

  const dateFrom = watch('dateFrom');
  const dateTo = watch('dateTo');

  // Day-granular key: `today` stays a stable reference within a day, so the
  // memos below don't recompute every render, while still advancing at midnight
  // so a dashboard left open across the day boundary can pick the new today.
  const todayKey = dayjs().format(DB_DATE_FORMAT);
  const today = useMemo(() => dayjs(todayKey), [todayKey]);

  const latestAllowedDateTo = useMemo(() => {
    const capEnd = dayjs(dateFrom).add(MAX_RANGE_DAYS - 1, 'day');

    return capEnd.isAfter(today) ? today : capEnd;
  }, [dateFrom, today]);

  // Only the To field is bounded by the other end; From stays free (up to today)
  // so the whole window can be moved back without first widening To.
  const onChangeDateFrom = useCallback(
    (value: Dayjs | null) => {
      if (!value) {
        return;
      }

      const capEnd = value.add(MAX_RANGE_DAYS - 1, 'day');
      const latest = capEnd.isAfter(today) ? today : capEnd;

      if (dayjs(dateTo).isBefore(value, 'day')) {
        setValue('dateTo', value);

        return;
      }

      if (dayjs(dateTo).isAfter(latest, 'day')) {
        setValue('dateTo', latest);
      }
    },
    [dateTo, setValue, today]
  );

  // A bound can be unparseable while it is being typed, and dayjs formats that
  // as the literal string "Invalid Date" — which reached the endpoint verbatim
  // in the query string and came back 422. An absent bound is still fine: the
  // endpoint defaults an omitted one to today.
  const isBoundUsable = (bound?: Dayjs | string | null): boolean =>
    !bound || dayjs(bound).isValid();

  const requestPayload = useMemo(
    () => ({
      dateFrom: dateFrom ? dayjs(dateFrom).format(DB_DATE_FORMAT) : undefined,
      dateTo: dateTo ? dayjs(dateTo).format(DB_DATE_FORMAT) : undefined,
    }),
    [dateFrom, dateTo]
  );

  const { report, isLoading, isFetching, isError, refetch } = useGetVendorQuoteImportRangeReport(
    requestPayload,
    { enabled: !!user?.admin && isBoundUsable(dateFrom) && isBoundUsable(dateTo) }
  );

  // keepPreviousData keeps isLoading false on range changes and refetches, so
  // isFetching is what tells the cards and the grid to show a loading state
  // instead of the previously selected range's numbers.
  const isDataLoading = isLoading || isFetching;

  const isTodayIncluded = !!dateTo && today.isSame(dayjs(dateTo), 'day');

  return (
    <Stack sx={{ gap: 2, height: '100%', minHeight: 0 }}>
      <Stack direction={Direction.ROW} sx={{ gap: 2, justifyContent: 'flex-end', paddingRight: 1 }}>
        <Box sx={{ width: FILTER_FIELD_WIDTH }}>
          <FormField
            name="dateFrom"
            type={InputType.DATE_INPUT}
            inputProps={{
              control,
              error: errors?.dateFrom,
              label: t('labelDateFrom'),
              maxDate: today,
              isClearable: false,
              onChange: onChangeDateFrom,
            }}
          />
        </Box>

        <Box sx={{ width: FILTER_FIELD_WIDTH }}>
          <FormField
            name="dateTo"
            type={InputType.DATE_INPUT}
            inputProps={{
              control,
              error: errors?.dateTo,
              label: t('labelDateTo'),
              minDate: dayjs(dateFrom),
              maxDate: latestAllowedDateTo,
              isClearable: false,
            }}
          />
        </Box>
      </Stack>

      {isError && (
        <ReportError
          title={t('errorTitleVendorQuoteLogReport')}
          message={t('messageVendorQuoteLogReportError')}
          onRetry={refetch}
        />
      )}

      {!isError && (
        <>
          <ReportDiagnostics report={report} />

          <StatCards summary={report?.summary} isLoading={isDataLoading} />

          <Box sx={{ flex: 1, minHeight: TABLE_MIN_HEIGHT }}>
            <VendorQuoteLogTable
              attempts={report?.attempts ?? []}
              isLoading={isDataLoading}
              showRefresh={isTodayIncluded}
              onRefresh={refetch}
            />
          </Box>
        </>
      )}
    </Stack>
  );
};

export const VendorQuoteLog = () => {
  const defaultValues = useMemo(() => {
    const today = dayjs();

    return { dateFrom: today, dateTo: today };
  }, []);

  return (
    <FormProvider schema={vendorQuoteLogSchema()} defaultValues={defaultValues}>
      <VendorQuoteLogContent />
    </FormProvider>
  );
};
